import { useState, useEffect, useCallback, useRef } from 'react';
import { clientsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Upload, Download, AlertCircle, CheckCircle, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cost_center_address: '',
    gst_number: '',
    contact_person: '',
    email: '',
    phone: '',
    default_currency: 'INR',
  });
  
  // Bulk upload states
  const [bulkStep, setBulkStep] = useState(1); // 1: Download, 2: Upload, 3: Results
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const currencies = ['INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD', 'AED'];

  const fetchClients = useCallback(async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
        toast.success('Client updated successfully');
      } else {
        await clientsAPI.create(formData);
        toast.success('Client created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || 'Operation failed');
      } else {
        toast.error(detail || 'Operation failed');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await clientsAPI.delete(id);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      cost_center_address: client.cost_center_address || '',
      gst_number: client.gst_number || '',
      contact_person: client.contact_person || '',
      email: client.email || '',
      phone: client.phone || '',
      default_currency: client.default_currency || 'INR',
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      cost_center_address: '',
      gst_number: '',
      contact_person: '',
      email: '',
      phone: '',
      default_currency: 'INR',
    });
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  // Bulk Upload Functions
  const handleDownloadTemplate = async () => {
    try {
      const response = await clientsAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
      setBulkStep(2);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleValidateUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const response = await clientsAPI.bulkUpload(selectedFile);
      setUploadResult(response.data);
      setBulkStep(3);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || 'Failed to validate file');
      } else {
        toast.error(detail || 'Failed to validate file');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const response = await clientsAPI.bulkConfirm(selectedFile);
      toast.success(response.data.message);
      setBulkDialogOpen(false);
      resetBulkUpload();
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || 'Failed to import clients');
      } else {
        toast.error(detail || 'Failed to import clients');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const response = await clientsAPI.bulkUpdate(selectedFile);
      toast.success(response.data.message);
      setBulkDialogOpen(false);
      resetBulkUpload();
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || 'Failed to update clients');
      } else {
        toast.error(detail || 'Failed to update clients');
      }
    } finally {
      setUploading(false);
    }
  };

  const resetBulkUpload = () => {
    setBulkStep(1);
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBulkDialogClose = (open) => {
    setBulkDialogOpen(open);
    if (!open) {
      resetBulkUpload();
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading clients...</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="clients-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Clients
          </h1>
          <p className="text-muted-foreground mt-2">Manage your client database</p>
        </div>
        <div className="flex gap-2">
          {/* Bulk Upload Button */}
          <Dialog open={bulkDialogOpen} onOpenChange={handleBulkDialogClose}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2" data-testid="bulk-upload-button">
                <Upload className="h-4 w-4" />
                Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="bulk-upload-dialog">
              <DialogHeader>
                <DialogTitle>Bulk Upload Clients</DialogTitle>
                <DialogDescription>
                  Import multiple clients at once using a CSV file
                </DialogDescription>
              </DialogHeader>
              
              {/* Step Indicator */}
              <div className="flex items-center justify-center gap-2 py-4">
                <div className={`flex items-center gap-2 ${bulkStep >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bulkStep >= 1 ? 'bg-primary text-white' : 'bg-muted'}`}>1</div>
                  <span className="text-sm font-medium">Download</span>
                </div>
                <div className={`w-12 h-0.5 ${bulkStep >= 2 ? 'bg-primary' : 'bg-muted'}`}></div>
                <div className={`flex items-center gap-2 ${bulkStep >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bulkStep >= 2 ? 'bg-primary text-white' : 'bg-muted'}`}>2</div>
                  <span className="text-sm font-medium">Upload</span>
                </div>
                <div className={`w-12 h-0.5 ${bulkStep >= 3 ? 'bg-primary' : 'bg-muted'}`}></div>
                <div className={`flex items-center gap-2 ${bulkStep >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bulkStep >= 3 ? 'bg-primary text-white' : 'bg-muted'}`}>3</div>
                  <span className="text-sm font-medium">Review</span>
                </div>
              </div>

              {/* Step 1: Download Template */}
              {bulkStep === 1 && (
                <div className="space-y-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <FileSpreadsheet className="h-6 w-6 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium">Step 1: Download the template</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Download the CSV template file and fill it with your client data. The template includes the required columns and an example row.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm font-medium mb-2">Template columns:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li><strong>name</strong> (required) - Client company name</li>
                      <li><strong>cost_center_address</strong> - Full address</li>
                      <li><strong>gst_number</strong> - GST registration number</li>
                      <li><strong>contact_person</strong> - Primary contact name</li>
                      <li><strong>email</strong> - Contact email</li>
                      <li><strong>phone</strong> - Contact phone</li>
                      <li><strong>default_currency</strong> - INR, USD, EUR, etc.</li>
                    </ul>
                  </div>
                  
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => handleBulkDialogClose(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleDownloadTemplate}>
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Upload File */}
              {bulkStep === 2 && (
                <div className="space-y-4">
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Upload className="h-6 w-6 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium">Step 2: Upload your filled template</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Select your filled CSV file. The system will validate the data and show any errors before importing.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <div className="border-2 border-dashed rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      ref={fileInputRef}
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-lg font-medium">
                        {selectedFile ? selectedFile.name : 'Click to select CSV file'}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        or drag and drop your file here
                      </p>
                    </label>
                  </div>
                  
                  <div className="flex gap-2 justify-between">
                    <Button variant="outline" onClick={() => setBulkStep(1)}>
                      Back
                    </Button>
                    <Button onClick={handleValidateUpload} disabled={!selectedFile || uploading}>
                      {uploading ? 'Validating...' : 'Validate & Preview'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Review Results */}
              {bulkStep === 3 && uploadResult && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4 flex items-center gap-3">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                        <div>
                          <p className="text-2xl font-bold text-green-700">{uploadResult.valid_count}</p>
                          <p className="text-sm text-green-600">New Clients</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-4 flex items-center gap-3">
                        <RefreshCw className="h-8 w-8 text-amber-600" />
                        <div>
                          <p className="text-2xl font-bold text-amber-700">{uploadResult.existing_count || 0}</p>
                          <p className="text-sm text-amber-600">Already Exist</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4 flex items-center gap-3">
                        <AlertCircle className="h-8 w-8 text-red-600" />
                        <div>
                          <p className="text-2xl font-bold text-red-700">{uploadResult.error_count}</p>
                          <p className="text-sm text-red-600">Errors</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Existing Clients Info */}
                  {(uploadResult.existing_count || 0) > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p className="text-sm text-amber-800">
                        <strong>{uploadResult.existing_count}</strong> client(s) already exist in the database. Click <strong>"Update Existing"</strong> to overwrite their details with the CSV data.
                      </p>
                    </div>
                  )}

                  {/* Real Errors (not "already exists") */}
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium text-destructive">Errors found:</p>
                      <div className="max-h-48 overflow-y-auto border rounded-lg">
                        {uploadResult.errors.map((error, idx) => (
                          <div key={`err-${error.row}-${idx}`} className="p-3 border-b last:border-b-0 bg-red-50">
                            <p className="font-medium text-sm">Row {error.row}: {error.data?.name || 'Unknown'}</p>
                            <ul className="text-sm text-red-600 mt-1">
                              {(error.errors || [error.error]).map((e, i) => (
                                <li key={`${error.row}-${i}-${String(e).slice(0, 20)}`}>• {e}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  {uploadResult.preview && uploadResult.preview.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium">Preview (first 5 new records):</p>
                      <div className="max-h-40 overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-muted">
                            <tr>
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">GST</th>
                              <th className="text-left p-2">Contact</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.preview.map((client, idx) => (
                              <tr key={`preview-${client.name || idx}-${idx}`} className="border-t">
                                <td className="p-2">{client.name}</td>
                                <td className="p-2">{client.gst_number || '-'}</td>
                                <td className="p-2">{client.contact_person || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 justify-between">
                    <Button variant="outline" onClick={() => { setBulkStep(2); setUploadResult(null); }}>
                      Back
                    </Button>
                    <div className="flex gap-2">
                      {(uploadResult.existing_count || 0) > 0 && (
                        <Button
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={handleBulkUpdate}
                          disabled={uploading}
                          data-testid="bulk-update-button"
                        >
                          {uploading ? 'Updating...' : `Update ${uploadResult.existing_count} Existing`}
                        </Button>
                      )}
                      <Button
                        onClick={handleConfirmUpload}
                        disabled={uploadResult.valid_count === 0 || uploading}
                        data-testid="bulk-import-button"
                      >
                        {uploading ? 'Importing...' : `Import ${uploadResult.valid_count} New Clients`}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* New Client Button */}
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2" data-testid="create-client-button">
                <Plus className="h-4 w-4" />
                New Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="client-dialog">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
                <DialogDescription>
                  {editingClient ? 'Update client details' : 'Add a new client to your database'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Client Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="client-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    value={formData.cost_center_address}
                    onChange={(e) => setFormData({ ...formData, cost_center_address: e.target.value })}
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gst">GST Number</Label>
                    <Input
                      id="gst"
                      value={formData.gst_number}
                      onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Default Currency</Label>
                    <select
                      id="currency"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.default_currency}
                      onChange={(e) => setFormData({ ...formData, default_currency: e.target.value })}
                    >
                      {currencies.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact">Contact Person</Label>
                  <Input
                    id="contact"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="client-submit-button">
                    {editingClient ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Clients List */}
      {clients.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              No clients yet. Add your first client to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {clients.map((client) => (
            <Card key={client.id} className="hover:shadow-md transition-shadow" data-testid={`client-card-${client.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl font-semibold">{client.name}</CardTitle>
                    {client.contact_person && (
                      <p className="text-sm text-muted-foreground mt-1">Contact: {client.contact_person}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(client)}
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(client.id)}
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">GST Number</p>
                    <p className="mt-1">{client.gst_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Currency</p>
                    <p className="mt-1">{client.default_currency || 'INR'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Email</p>
                    <p className="mt-1">{client.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Phone</p>
                    <p className="mt-1">{client.phone || '-'}</p>
                  </div>
                </div>
                {client.cost_center_address && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Address</p>
                    <p className="mt-1 text-sm">{client.cost_center_address}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Clients;
